/**
 * User Hooks
 * TanStack Query hooks for user data
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
    getUsers,
    getUserPersonalDetail,
    getUserTransactionHistory,
    flagUser,
    unflagUser,
} from '@/services/users.service'
import type {
    UserListResponse,
    UserFilters,
    UserPersonalDetailResponse,
    UserTransactionHistoryResponse,
    UserTransactionFilters,
} from '@/types/user'

// Legacy type for backward compatibility
export interface User {
    id: string
    accountName: string
    walletAddress: string
    email: string
    phoneNo: string
    dateJoined: string
    gender: 'M' | 'F'
    status: 'Active' | 'Flagged'
    // Additional fields for account detail modal
    userId: string
    phoneNumber: string
    dateOfBirth: string
    countryOfResidence: string
    residentialAddress: string
    totalTransactions: number
    amountSent: number
    amountReceived: number
    amountBought: number
    amountSold: number
}

export interface UserTransaction {
    id: string
    date: string
    txnType: 'Buy' | 'Sell' | 'Convert' | 'Receive' | 'Send'
    amount: string
    token: 'BTC' | 'USDT' | 'USDC' | 'ETH' | 'DOGE'
    status: 'Completed' | 'Pending' | 'Failed' | 'Cancelled'
}

/**
 * Hook to fetch users list with filters
 * GET /api/v1/admin/users
 * @param filters - Optional query parameters for filtering users
 */
export const useUsers = (filters?: UserFilters) => {
    return useQuery<UserListResponse>({
        queryKey: ['users', filters],
        queryFn: () => getUsers(filters),
        staleTime: 1000 * 30, // 30 seconds
    })
}

/**
 * Hook for fetching user personal details
 * GET /api/v1/admin/users/{id}/personal-detail
 * @param id - User ID
 */
export const useUserPersonalDetail = (id: string) => {
    return useQuery<UserPersonalDetailResponse>({
        queryKey: ['user', 'personal-detail', id],
        queryFn: () => getUserPersonalDetail(id),
        enabled: !!id,
        staleTime: 1000 * 60,
    })
}

/**
 * Hook for fetching user transaction history
 * GET /api/v1/admin/users/{id}/user-transactions-history
 * @param id - User ID
 * @param filters - Optional transaction filters
 */
export const useUserTransactionHistory = (
    id: string,
    filters?: UserTransactionFilters
) => {
    return useQuery<UserTransactionHistoryResponse>({
        queryKey: ['user', 'transactions', id, filters],
        queryFn: () => getUserTransactionHistory(id, filters),
        enabled: !!id,
        staleTime: 1000 * 30, // 30 seconds
    })
}

/**
 * Hook for flagging a user
 * PATCH /api/v1/admin/users/{id}/flag
 */
export const useFlagUser = () => {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: ({ userId, reason }: { userId: string; reason: string }) =>
            flagUser(userId, { reason }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['users'] })
            queryClient.invalidateQueries({ queryKey: ['user'] })
        },
    })
}

/**
 * Hook for unflagging a user
 * PATCH /api/v1/admin/users/{id}/unflag
 */
export const useUnflagUser = () => {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: (userId: string) => unflagUser(userId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['users'] })
            queryClient.invalidateQueries({ queryKey: ['user'] })
        },
    })
}

// Hook for downloading user transaction history
export const useDownloadTransactionHistory = () => {
    return useMutation({
        mutationFn: async (userIds: string[]) => {
            // TODO: Implement transaction history download logic
            console.log('Downloading transaction history for:', userIds)
        },
    })
}
